import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import api from '../api/axios';

/* ------------------------------------------------------------------
   /career-resources  –  hub page
   SEO title: Career Advice, Resume Tips & Interview Preparation | PlanB Jobs

   HOW IT WORKS
   - CATEGORIES and TOPICS below are the editorial plan from the brief
     (titles, slugs, categories only). No dates, authors or body text are
     invented here.
   - Published articles come from the API (GET /articles). A topic whose slug
     is found in the API response becomes a link with its real author,
     published date and updated date. A topic not published yet shows under
     "On the way" as "Coming soon" and is not clickable.
   - Each article lives at /career-resources/:slug (separate article page:
     original content, author, published date, updated date, related job links).

   ARTICLE FIELDS READ: slug, title, excerpt, category (slug), author_name,
   published_at, updated_at, read_minutes
------------------------------------------------------------------- */

// audience: who the category is mainly for ('seekers' | 'employers' | 'both')
const CATEGORIES = [
  { slug: 'resume-cv-writing', name: 'Resume & CV Writing', icon: 'doc', audience: 'seekers', blurb: 'Formats, wording and layout that get read.' },
  { slug: 'interview-preparation', name: 'Interview Preparation', icon: 'chat', audience: 'seekers', blurb: 'Prepare answers and handle the room.' },
  { slug: 'fresher-career-guide', name: 'Fresher Career Guide', icon: 'cap', audience: 'seekers', blurb: 'Where to start when you have no experience.', jobs: '/jobs?experience=fresher', jobsLabel: 'Fresher jobs' },
  { slug: 'job-search-strategies', name: 'Job Search Strategies', icon: 'target', audience: 'seekers', blurb: 'Search smarter and apply with purpose.', jobs: '/jobs', jobsLabel: 'Browse jobs' },
  { slug: 'salary-career-growth', name: 'Salary & Career Growth', icon: 'chart', audience: 'seekers', blurb: 'Pay, promotions and long-term planning.' },
  { slug: 'work-from-home-guidance', name: 'Work From Home Guidance', icon: 'home', audience: 'seekers', blurb: 'Find remote roles and avoid fake offers.', jobs: '/jobs?work_mode=work_from_home', jobsLabel: 'Work from home jobs' },
  { slug: 'employer-hiring-tips', name: 'Employer Hiring Tips', icon: 'briefcase', audience: 'employers', blurb: 'Practical advice for hiring well.', jobs: '/employers', jobsLabel: 'Hire on PlanB Jobs' },
  { slug: 'hr-recruitment-insights', name: 'HR & Recruitment Insights', icon: 'users', audience: 'employers', blurb: 'What is changing in recruitment.' },
  { slug: 'industry-career-guides', name: 'Industry Career Guides', icon: 'building', audience: 'both', blurb: 'Roles, skills and paths by industry.' },
  { slug: 'workplace-skills', name: 'Workplace Skills', icon: 'bolt', audience: 'both', blurb: 'Skills that matter once you are hired.' },
];

const TOPICS = [
  { slug: 'how-to-find-your-first-job-in-india', title: 'How to Find Your First Job in India', category: 'fresher-career-guide' },
  { slug: 'best-resume-format-for-freshers-in-india', title: 'Best Resume Format for Freshers in India', category: 'resume-cv-writing' },
  { slug: 'how-to-prepare-for-your-first-job-interview', title: 'How to Prepare for Your First Job Interview', category: 'interview-preparation' },
  { slug: 'top-skills-employers-look-for-in-2026', title: 'Top Skills Employers Look for in 2026', category: 'workplace-skills' },
  { slug: 'how-to-find-jobs-without-experience', title: 'How to Find Jobs Without Experience', category: 'fresher-career-guide' },
  { slug: 'how-to-write-a-professional-resume', title: 'How to Write a Professional Resume', category: 'resume-cv-writing' },
  { slug: 'common-interview-questions-and-answers', title: 'Common Interview Questions and Answers', category: 'interview-preparation' },
  { slug: 'how-to-find-genuine-work-from-home-jobs', title: 'How to Find Genuine Work From Home Jobs', category: 'work-from-home-guidance' },
  { slug: 'how-employers-can-hire-candidates-online', title: 'How Employers Can Hire Candidates Online', category: 'employer-hiring-tips' },
  { slug: 'how-to-improve-your-job-application-success', title: 'How to Improve Your Job Application Success', category: 'job-search-strategies' },
];

const AUDIENCES = [
  { key: 'all', label: 'Everyone' },
  { key: 'seekers', label: 'Job seekers' },
  { key: 'employers', label: 'Employers' },
];

const ICONS = {
  doc: <><path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
  chat: <path d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />,
  cap: <><path d="M2 9l10-5 10 5-10 5-10-5z" /><path d="M6 11v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" /></>,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.2" fill="currentColor" /></>,
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  home: <path d="M3 11l9-8 9 8v10a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1V11z" />,
  briefcase: <><rect x="2" y="7" width="20" height="13" rx="2" /><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2M2 12h20" /></>,
  users: <><circle cx="9" cy="8" r="3.5" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" /><path d="M16 4.5a3.5 3.5 0 010 7M18 15c2.5.6 4 2.6 4 6" /></>,
  building: <><rect x="4" y="3" width="10" height="18" /><rect x="14" y="9" width="6" height="12" /><path d="M7 7h1M11 7h1M7 11h1M11 11h1M7 15h1M11 15h1" /></>,
  bolt: <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
};

function Icon({ name, className = 'w-full h-full' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

const catOf = (slug) => CATEGORIES.find((c) => c.slug === slug);
const catName = (slug) => catOf(slug)?.name || '';

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ------------------------------ meta line ------------------------------ */
function ByLine({ item, light = false }) {
  const published_on = fmtDate(item.published_at);
  const updated_on = fmtDate(item.updated_at);
  const muted = light ? 'text-white/70' : 'text-gray-500';
  const strong = light ? 'text-white' : 'text-[#1E0F3D]';
  return (
    <div className={`text-xs ${muted}`}>
      <p>
        {item.author_name && <span className={`font-semibold ${strong}`}>{item.author_name}</span>}
        {item.author_name && published_on && <span className="opacity-50"> · </span>}
        {published_on && <time dateTime={item.published_at}>{published_on}</time>}
      </p>
      {updated_on && updated_on !== published_on && (
        <p className="mt-0.5 opacity-80">Updated <time dateTime={item.updated_at}>{updated_on}</time></p>
      )}
    </div>
  );
}

/* ------------------------------ featured article ------------------------------ */
function FeaturedCard({ item }) {
  return (
    <Link
      to={`/career-resources/${item.slug}`}
      className="group relative block overflow-hidden rounded-[2rem] brand-gradient text-white p-8 lg:p-12 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)] transition hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#7C3AED]/40"
    >
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-2xl">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs font-bold bg-white text-[#7C3AED] rounded-full px-3 py-1">Latest guide</span>
          <span className="text-xs font-semibold bg-white/20 rounded-full px-3 py-1">{catName(item.category)}</span>
          {item.read_minutes && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/85">
              <span className="w-3.5 h-3.5"><Icon name="clock" /></span>{item.read_minutes} min read
            </span>
          )}
        </div>
        <h3 className="text-3xl sm:text-4xl font-extrabold leading-tight tracking-tight mb-4">{item.title}</h3>
        {item.excerpt && <p className="text-white/85 leading-relaxed mb-6 line-clamp-3">{item.excerpt}</p>}
        <ByLine item={item} light />
        <span className="inline-flex items-center gap-2 mt-6 bg-white text-[#7C3AED] px-6 py-3 rounded-xl font-bold text-sm">
          Read the guide <span className="w-4 h-4 transition-transform group-hover:translate-x-1"><Icon name="arrow" /></span>
        </span>
      </div>
    </Link>
  );
}

/* ------------------------------ article card ------------------------------ */
function ArticleCard({ item }) {
  const cat = catOf(item.category);
  return (
    <Link
      to={`/career-resources/${item.slug}`}
      className="group flex flex-col h-full rounded-3xl p-6 bg-white border border-purple-100 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#7C3AED]/50 hover:shadow-[0_30px_60px_-25px_rgba(124,58,237,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#F5F0FA] rounded-full pl-1.5 pr-3 py-1">
          {cat && <span className="w-5 h-5 rounded-full brand-gradient text-white p-1"><Icon name={cat.icon} /></span>}
          {catName(item.category)}
        </span>
        {item.read_minutes && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
            <span className="w-3.5 h-3.5"><Icon name="clock" /></span>{item.read_minutes} min
          </span>
        )}
      </div>

      <h3 className="text-lg font-bold leading-snug text-[#1E0F3D] mb-2">{item.title}</h3>
      {item.excerpt && <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">{item.excerpt}</p>}

      <div className="mt-auto pt-5">
        <ByLine item={item} />
        <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#DB2777]">
          Read article <span className="w-4 h-4 transition-transform group-hover:translate-x-1"><Icon name="arrow" /></span>
        </span>
      </div>
    </Link>
  );
}

/* ------------------------------ coming-soon row ------------------------------ */
function SoonRow({ item }) {
  const cat = catOf(item.category);
  return (
    <li className="flex items-center gap-4 rounded-2xl border border-dashed border-purple-200 bg-[#FBF5FF]/60 px-5 py-4">
      <span className="w-10 h-10 rounded-xl bg-white border border-purple-100 text-[#7C3AED] p-2.5 shrink-0">
        <Icon name={cat?.icon || 'doc'} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-[#1E0F3D] leading-snug">{item.title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{catName(item.category)}</p>
      </div>
      <span className="text-[11px] font-bold text-[#7C3AED] bg-white border border-purple-100 rounded-full px-3 py-1 whitespace-nowrap">Coming soon</span>
    </li>
  );
}

/* ------------------------------ page ------------------------------ */
function CareerResources() {
  const [params, setParams] = useSearchParams();
  const active = params.get('category') || 'all';
  const [audience, setAudience] = useState('all');
  const [query, setQuery] = useState('');
  const [apiArticles, setApiArticles] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const load = () => {
    setStatus('loading');
    api.get('/articles')
      .then((res) => {
        setApiArticles(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
        setStatus('ready');
      })
      .catch(() => {
        setApiArticles([]);
        setStatus('error');
      });
  };
  useEffect(load, []);

  // Merge the editorial plan with what is actually published
  const items = useMemo(() => {
    const bySlug = new Map(apiArticles.map((a) => [a.slug, a]));
    const planned = TOPICS.map((t) => {
      const live = bySlug.get(t.slug);
      return live ? { ...t, ...live, category: live.category || t.category, published: true } : { ...t, published: false };
    });
    const extra = apiArticles
      .filter((a) => !TOPICS.some((t) => t.slug === a.slug))
      .map((a) => ({ ...a, published: true }));
    const pub = [...planned.filter((i) => i.published), ...extra].sort(
      (a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0)
    );
    return [...pub, ...planned.filter((i) => !i.published)];
  }, [apiArticles]);

  // Per-category counts of published guides
  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => { if (i.published) c[i.category] = (c[i.category] || 0) + 1; });
    return c;
  }, [items]);
  const totals = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.category] = (c[i.category] || 0) + 1; });
    return c;
  }, [items]);

  const audienceMatch = (slug) => {
    if (audience === 'all') return true;
    const a = catOf(slug)?.audience;
    return a === 'both' || a === audience;
  };

  const visible = items.filter((i) => {
    if (active !== 'all' && i.category !== active) return false;
    if (!audienceMatch(i.category)) return false;
    if (query.trim() && !i.title.toLowerCase().includes(query.trim().toLowerCase())) return false;
    return true;
  });
  const pubVisible = visible.filter((i) => i.published);
  const soonVisible = visible.filter((i) => !i.published);

  const showFeatured = active === 'all' && audience === 'all' && !query.trim() && pubVisible.length > 0;
  const featured = showFeatured ? pubVisible[0] : null;
  const gridPub = featured ? pubVisible.slice(1) : pubVisible;

  const shownCategories = CATEGORIES.filter((c) => audienceMatch(c.slug));
  const activeCat = catOf(active);
  const totalPublished = items.filter((i) => i.published).length;

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === 'all') next.delete('category'); else next.set('category', slug);
    setParams(next, { replace: true });
  };
  const goToArticles = () => document.getElementById('articles')?.scrollIntoView({ behavior: 'smooth' });
  const reset = () => { setQuery(''); setAudience('all'); setCategory('all'); };

  // SEO
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Career Advice, Resume Tips & Interview Preparation | PlanB Jobs';
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute('content');
    meta.setAttribute(
      'content',
      'Explore career advice, resume writing tips, interview preparation, job search guidance and recruitment insights from PlanB Jobs.'
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="resources-root min-h-screen bg-white overflow-x-clip text-[#1E0F3D]">
      <style>{`
        .brand-gradient { background-image: linear-gradient(90deg,#7C3AED,#DB2777); }
        .resources-root h2 {
          display: inline-block;
          background-image: linear-gradient(90deg,#7C3AED,#DB2777);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        .resources-root .brand-gradient h2 { background-image: none; -webkit-text-fill-color: #fff; color: #fff; }
        @keyframes rise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
        @keyframes swap { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes pulse-soft { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
        .rise { animation: rise .8s cubic-bezier(.2,.8,.2,1) both; }
        .swap { animation: swap .35s ease-out both; }
        .skeleton { animation: pulse-soft 1.4s ease-in-out infinite; }
        .shine-btn { position: relative; overflow: hidden; }
        .shine-btn::after { content: ''; position: absolute; top: 0; left: 0; width: 30%; height: 100%; background: rgba(255,255,255,.3); transform: translateX(-120%) skewX(-20deg); transition: transform .7s ease; pointer-events: none; }
        .shine-btn:hover::after { transform: translateX(420%) skewX(-20deg); }
        .no-scrollbar { scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        html { scroll-behavior: smooth; }
        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
          .rise, .swap, .skeleton { animation: none; }
          .shine-btn::after { transition: none; }
        }
      `}</style>

      <Navbar user={user} />

      {/* ================================ HERO ================================ */}
      <section className="relative bg-gradient-to-br from-[#FBF5FF] via-white to-[#FDF0F6] pt-14 pb-16 lg:pt-20 lg:pb-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#7C3AED]/15 blur-3xl" />
          <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#DB2777]/12 blur-3xl" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-12 lg:gap-14 items-center">
          <div className="rise">
            <h1 className="mb-6 text-[40px] sm:text-[56px] font-extrabold leading-[1.05] tracking-tight">
              <span className="block w-fit bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">Your Career Growth</span>
              <span className="block w-fit bg-gradient-to-r from-[#7C3AED] to-[#DB2777] bg-clip-text text-transparent">Starts Here.</span>
            </h1>
            <p className="max-w-lg text-lg leading-7 text-gray-600">
              Finding a job is only one part of building a career. Explore useful resources to improve your skills, prepare for interviews and make better career decisions.
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); goToArticles(); }}
              className="flex items-center gap-2 mt-8 max-w-lg bg-white rounded-2xl border border-purple-100 p-2 shadow-[0_20px_50px_-25px_rgba(124,58,237,0.45)] focus-within:ring-2 focus-within:ring-[#7C3AED]/40"
              role="search"
            >
              <span className="w-5 h-5 ml-3 text-[#7C3AED] shrink-0"><Icon name="search" /></span>
              <label htmlFor="res-search" className="sr-only">Search guides</label>
              <input
                id="res-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search guides, e.g. resume or interview"
                className="flex-1 min-w-0 bg-transparent px-2 py-2.5 text-sm focus:outline-none"
              />
              <button type="submit" className="shine-btn brand-gradient text-white px-6 py-2.5 rounded-xl font-semibold text-sm shrink-0">Search</button>
            </form>
          </div>

          {/* "Who are you?" quick paths */}
          <div className="rise relative max-w-md w-full" style={{ animationDelay: '.2s' }}>
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#7C3AED]/25 to-[#DB2777]/20 blur-3xl" aria-hidden="true" />
            <div className="relative bg-white rounded-3xl border border-purple-100 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.45)] p-6">
              <p className="font-bold text-lg mb-1">Where would you like to start?</p>
              <p className="text-sm text-gray-500 mb-5">Choose a path and we will show the right guides.</p>
              <div className="space-y-3">
                {[
                  { key: 'seekers', title: 'I am looking for a job', sub: 'Resumes, interviews, job search', icon: 'target' },
                  { key: 'employers', title: 'I am hiring', sub: 'Hiring tips and recruitment insights', icon: 'briefcase' },
                ].map((p) => (
                  <button
                    key={p.key} type="button"
                    onClick={() => { setAudience(p.key); setCategory('all'); goToArticles(); }}
                    className="group w-full flex items-center gap-4 text-left rounded-2xl border border-purple-100 bg-gradient-to-br from-[#FBF5FF] to-white p-4 transition hover:-translate-y-0.5 hover:border-[#7C3AED]/50 hover:shadow-[0_20px_40px_-25px_rgba(124,58,237,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
                  >
                    <span className="w-11 h-11 rounded-xl brand-gradient text-white p-2.5 shrink-0"><Icon name={p.icon} /></span>
                    <span className="flex-1">
                      <span className="block font-bold text-sm">{p.title}</span>
                      <span className="block text-xs text-gray-500">{p.sub}</span>
                    </span>
                    <span className="w-4 h-4 text-[#DB2777] transition-transform group-hover:translate-x-1"><Icon name="arrow" /></span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-5">
                {CATEGORIES.length} topics{status === 'ready' ? ` · ${totalPublished} guide${totalPublished === 1 ? '' : 's'} published` : ''}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ BROWSE BY TOPIC ============================ */}
      <section id="topics" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Browse by topic</h2>
            <p className="text-gray-500 max-w-md">Ten areas of career and hiring advice, for job seekers and employers.</p>
          </div>
          <div className="inline-flex bg-[#F5F0FA] rounded-full p-1 self-start" role="group" aria-label="Show topics for">
            {AUDIENCES.map((a) => (
              <button
                key={a.key} type="button" aria-pressed={audience === a.key} onClick={() => setAudience(a.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 ${
                  audience === a.key ? 'brand-gradient text-white shadow-md shadow-[#7C3AED]/25' : 'text-gray-600 hover:text-[#7C3AED]'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {shownCategories.map((c) => {
            const on = active === c.slug;
            const live = counts[c.slug] || 0;
            return (
              <button
                key={c.slug} type="button" aria-pressed={on}
                onClick={() => { setCategory(on ? 'all' : c.slug); if (!on) goToArticles(); }}
                className={`group flex flex-col text-left rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 ${
                  on
                    ? 'border-[#DB2777] bg-gradient-to-br from-[#FDF4FF] to-white shadow-[0_20px_40px_-20px_rgba(124,58,237,0.5)]'
                    : 'border-purple-100 bg-white hover:border-[#DB2777]/50 hover:shadow-[0_20px_40px_-20px_rgba(124,58,237,0.5)]'
                }`}
              >
                <span className="w-11 h-11 rounded-xl brand-gradient text-white p-2.5 shadow-md shadow-[#7C3AED]/25 mb-4"><Icon name={c.icon} /></span>
                <span className="font-bold text-sm leading-snug mb-1.5">{c.name}</span>
                <span className="text-xs text-gray-500 leading-relaxed mb-3">{c.blurb}</span>
                <span className="mt-auto text-xs font-semibold text-[#DB2777]">
                  {status === 'ready'
                    ? (live > 0 ? `${live} guide${live === 1 ? '' : 's'}` : 'Coming soon')
                    : `${totals[c.slug] || 0} planned`}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ============================== GUIDES ============================== */}
      <section id="articles" className="scroll-mt-24 bg-gradient-to-br from-[#FBF5FF] to-[#FDF0F6] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{activeCat ? activeCat.name : 'Latest guides'}</h2>
              <p className="text-gray-500 max-w-lg">{activeCat ? activeCat.blurb : 'Practical, original advice from PlanB Jobs.'}</p>
            </div>
            {(active !== 'all' || audience !== 'all' || query.trim()) && (
              <div className="flex flex-wrap items-center gap-2">
                {active !== 'all' && <span className="text-xs font-semibold text-[#7C3AED] bg-white border border-purple-100 rounded-full px-3 py-1.5">{catName(active)}</span>}
                {audience !== 'all' && <span className="text-xs font-semibold text-[#7C3AED] bg-white border border-purple-100 rounded-full px-3 py-1.5">{AUDIENCES.find((a) => a.key === audience)?.label}</span>}
                {query.trim() && <span className="text-xs font-semibold text-[#7C3AED] bg-white border border-purple-100 rounded-full px-3 py-1.5">“{query.trim()}”</span>}
                <button type="button" onClick={reset} className="text-sm font-bold text-[#DB2777] hover:underline">Clear all</button>
              </div>
            )}
          </div>

          {/* mobile-friendly category chips */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-8 -mx-6 px-6 lg:mx-0 lg:px-0 lg:flex-wrap" role="group" aria-label="Filter by category">
            {[{ slug: 'all', name: 'All' }, ...shownCategories].map((c) => {
              const on = active === c.slug;
              return (
                <button
                  key={c.slug} type="button" aria-pressed={on} onClick={() => setCategory(c.slug)}
                  className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50 ${
                    on ? 'brand-gradient text-white border-transparent shadow-md shadow-[#7C3AED]/25' : 'bg-white text-[#7C3AED] border-[#7C3AED]/30 hover:bg-[#F5F0FA]'
                  }`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>

          {status === 'error' && (
            <div className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-purple-100 px-5 py-4 text-sm" role="status">
              <span className="text-gray-600">We could not load published guides right now.</span>
              <button type="button" onClick={load} className="font-bold text-[#DB2777] hover:underline">Try again</button>
            </div>
          )}

          {status === 'loading' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" aria-busy="true" aria-label="Loading guides">
              {[0, 1, 2].map((i) => <div key={i} className="skeleton h-64 rounded-3xl bg-white/80" />)}
            </div>
          ) : (
            <div key={`${active}-${audience}-${query}`} className="swap">
              {featured && <div className="mb-8"><FeaturedCard item={featured} /></div>}

              {gridPub.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {gridPub.map((item) => <ArticleCard key={item.slug} item={item} />)}
                </div>
              )}

              {pubVisible.length === 0 && soonVisible.length > 0 && (
                <p className="text-sm text-gray-500 mb-6">No guides are published in this area yet. They are on the way.</p>
              )}

              {soonVisible.length > 0 && (
                <div className={pubVisible.length > 0 ? 'mt-12' : ''}>
                  <h3 className="text-lg font-bold mb-4">On the way</h3>
                  <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {soonVisible.map((item) => <SoonRow key={item.slug} item={item} />)}
                  </ul>
                </div>
              )}

              {visible.length === 0 && (
                <div className="text-center py-14">
                  <p className="font-bold text-lg mb-1">No guides match your search.</p>
                  <p className="text-sm text-gray-500 mb-5">Try a different word or choose another topic.</p>
                  <button type="button" onClick={reset} className="shine-btn brand-gradient text-white px-8 py-3 rounded-xl font-semibold text-sm">
                    Show all guides
                  </button>
                </div>
              )}
            </div>
          )}

          {activeCat?.jobs && (
            <div className="mt-10 flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-purple-100 px-6 py-5">
              <p className="text-sm text-gray-600">Ready to act on what you read?</p>
              <Link to={activeCat.jobs} className="inline-flex items-center gap-1.5 font-bold text-sm text-[#DB2777] hover:underline">
                {activeCat.jobsLabel} <span className="w-4 h-4"><Icon name="arrow" /></span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ============================== FINAL CTA ============================== */}
      <section className="px-6 lg:px-8 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto brand-gradient rounded-[2rem] relative overflow-hidden px-8 lg:px-16 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
          <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Put your preparation to work.</h2>
            <p className="text-white/85 max-w-md">Explore relevant jobs, or hire people for your business.</p>
          </div>
          <div className="relative flex flex-wrap gap-3 lg:justify-self-end">
            <Link to="/jobs" className="bg-white text-[#7C3AED] px-8 py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition">Find Jobs</Link>
            <Link to="/employers" className="border border-white/50 text-white px-8 py-3.5 rounded-xl font-semibold text-sm hover:bg-white/10 transition">For Employers</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CareerResources;